import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";

import type { Exercise } from "../../../src/types/contents/exercise";
import {
  createExercise,
  getExercise,
  updateExercise,
  uploadMedia,
} from "../api";
import { auth } from "../auth";

import { MediaWithPreview, TagsSelector } from "./Common";
import { MultiSelect } from "./MultiSelect";

const emptyExercise: Exercise<"api"> = {
  title: "",
  description: "",
  category: "",
  language: "",
  skillLevel: 0,
  primaryMuscles: [],
  secondaryMuscles: [],
  tips: "",
  variations: "",
  media: "",
  media2: "",
  video: "",
  sportTitle: "",
  tags: [],
  favorite: false,
};

export const ExerciseComponent = () => {
  const [exercise, setExercise] = useState<Exercise<"api"> | null>(null);
  const [mediaPreview, setMediaPreview] = useState<File | null>(null);
  const [mediaIdOverride, setMediaIdOverride] = useState<boolean>(false);
  const [media2Preview, setMedia2Preview] = useState<File | null>(null);
  const [media2IdOverride, setMedia2IdOverride] = useState<boolean>(false);
  const [videoPreview, setVideoPreview] = useState<File | null>(null);
  const [videoIdOverride, setVideoIdOverride] = useState<boolean>(false);

  const { id } = useParams();
  const isEdit = id !== undefined && id !== "new";
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (isEdit) {
        setExercise(await getExercise(id));
      } else {
        setExercise({
          ...emptyExercise,
        });
      }
    };

    if (auth.currentUser) void init();
  }, []);

  if (exercise === null) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">Loading...</Typography>
      </Stack>
    );
  }

  const upsertMedia = async () => {
    if (mediaPreview) {
      const uploadResult = await uploadMedia(
        mediaPreview,
        mediaIdOverride ? exercise.media : undefined,
      );
      setMediaPreview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return exercise.media;
  };

  const upsertMedia2 = async () => {
    if (media2Preview) {
      const uploadResult = await uploadMedia(
        media2Preview,
        media2IdOverride ? exercise.media2 : undefined,
      );
      setMedia2Preview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return exercise.media2;
  };

  const upsertVideo = async () => {
    if (videoPreview) {
      const uploadResult = await uploadMedia(
        videoPreview,
        videoIdOverride ? exercise.video : undefined,
      );
      setVideoPreview(null);
      if (uploadResult.public_id) {
        return uploadResult.public_id;
      }
    }
    return exercise.video;
  };

  const save = async () => {
    try {
      const media = await upsertMedia();
      const media2 = await upsertMedia2();
      const video = await upsertVideo();
      const e: Exercise<"api"> & { favorite: boolean } = {
        ...exercise,
        media,
        media2,
        video,
      };
      if (isEdit) {
        await updateExercise(e);
      } else {
        const { id: newId } = await createExercise(e);
        if (newId) {
          navigate(`/exercise/${newId}`);
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        alert(e.message);
      }
    }
  };

  return (
    <Stack spacing={2}>
      <TextField
        label="Title"
        value={exercise.title}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, title: input.currentTarget.value });
        }}
      />
      <TextField
        label="Description"
        value={exercise.description}
        variant="outlined"
        onChange={(input) => {
          setExercise({
            ...exercise,
            description: input.currentTarget.value,
          });
        }}
      />
      <TextField
        label="Category"
        value={exercise.category}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, category: input.currentTarget.value });
        }}
      />
      <TextField
        label="Language"
        value={exercise.language}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, language: input.currentTarget.value });
        }}
      />
      <TextField
        type="number"
        value={exercise.skillLevel}
        label="Skill level"
        variant="outlined"
        onChange={(input) => {
          setExercise({
            ...exercise,
            skillLevel: parseInt(input.currentTarget.value),
          });
        }}
      />
      <Stack direction="row" spacing={2}>
        <MultiSelect
          label="Primary muscles"
          value={exercise.primaryMuscles}
          options={[
            { value: "chest", label: "Chest" },
            { value: "back", label: "Back" },
            { value: "legs", label: "Legs" },
            { value: "arms", label: "Arms" },
          ]}
          onChange={(selected) => {
            console.log(selected);
            setExercise({
              ...exercise,
              primaryMuscles: selected,
            });
          }}
        />
        <MultiSelect
          label="Secondary muscles"
          value={exercise.secondaryMuscles}
          options={[
            { value: "chest", label: "Chest" },
            { value: "back", label: "Back" },
            { value: "legs", label: "Legs" },
            { value: "arms", label: "Arms" },
          ]}
          onChange={(selected) => {
            setExercise({
              ...exercise,
              secondaryMuscles: selected,
            });
          }}
        />
      </Stack>
      <TextField
        label="Tips"
        value={exercise.tips}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, tips: input.currentTarget.value });
        }}
      />
      <TextField
        label="Variations"
        value={exercise.variations}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, variations: input.currentTarget.value });
        }}
      />
      <MediaWithPreview
        type="image"
        label="Upload media"
        alt="Exercise media"
        mediaId={exercise.media}
        mediaPreview={mediaPreview}
        setMediaPreview={setMediaPreview}
        mediaIdOverride={mediaIdOverride}
        setMediaIdOverride={setMediaIdOverride}
        overrideAble={isEdit}
      />
      <MediaWithPreview
        type="image"
        label="Upload media 2"
        alt="Exercise media 2"
        mediaId={exercise.media2}
        mediaPreview={media2Preview}
        setMediaPreview={setMedia2Preview}
        mediaIdOverride={media2IdOverride}
        setMediaIdOverride={setMedia2IdOverride}
        overrideAble={isEdit}
      />
      <MediaWithPreview
        type="video"
        label="Upload video"
        alt="Exercise video"
        mediaId={exercise.video}
        mediaPreview={videoPreview}
        setMediaPreview={setVideoPreview}
        mediaIdOverride={videoIdOverride}
        setMediaIdOverride={setVideoIdOverride}
        overrideAble={isEdit}
      />
      <TextField
        label="Sport title"
        value={exercise.sportTitle}
        variant="outlined"
        onChange={(input) => {
          setExercise({ ...exercise, sportTitle: input.currentTarget.value });
        }}
      />
      <Stack direction="row" spacing={2}>
        <TagsSelector
          value={exercise.tags ?? []}
          onChange={(selected) => {
            setExercise({
              ...exercise,
              tags: selected,
            });
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={exercise.favorite}
              onChange={(e) =>
                setExercise({ ...exercise, favorite: e.target.checked })
              }
            />
          }
          label="Favorite"
        />
      </Stack>
      <Button variant="contained" onClick={() => save()}>
        {exercise.id ? "Save" : "Create"}
      </Button>
    </Stack>
  );
};
